import { createContext, useCallback, useContext, useId, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Checkbox } from '~/components/ui/checkbox';

type ConfirmOptions = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
};

type ConfirmOption = { label: string; defaultChecked?: boolean };

export type ConfirmWithOptionResult = { confirmed: false } | { confirmed: true; checked: boolean };

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

/**
 * Run `act` at once when there is nothing to ask (`ask` is null), and only
 * after a yes otherwise. Resolves to whether it ran. One helper so a "no"
 * can never fall through to the action at any of its call sites.
 */
export async function runIfConfirmed(
  confirm: ConfirmFn,
  ask: ConfirmOptions | null,
  act: () => void,
): Promise<boolean> {
  if (ask && !(await confirm(ask))) return false;
  act();
  return true;
}
type ConfirmWithOptionFn = (options: ConfirmOptions & { option: ConfirmOption }) => Promise<ConfirmWithOptionResult>;
type OpenFn = (options: ConfirmOptions & { option?: ConfirmOption }) => Promise<ConfirmWithOptionResult>;

const ConfirmContext = createContext<OpenFn | null>(null);

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<(ConfirmOptions & { option?: ConfirmOption }) | null>(null);
  const [checked, setChecked] = useState(false);
  const resolverRef = useRef<((value: ConfirmWithOptionResult) => void) | null>(null);
  const optionId = useId();

  const openConfirm = useCallback<OpenFn>((opts) => {
    setOptions(opts);
    setChecked(opts.option?.defaultChecked ?? false);
    setOpen(true);
    return new Promise<ConfirmWithOptionResult>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleResolve = (confirmed: boolean) => {
    resolverRef.current?.(confirmed ? { confirmed: true, checked } : { confirmed: false });
    resolverRef.current = null;
    setOpen(false);
  };

  return (
    <ConfirmContext.Provider value={openConfirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleResolve(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title}</AlertDialogTitle>
            {options?.description && (
              <AlertDialogDescription>{options.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {options?.option && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={optionId}
                checked={checked}
                onCheckedChange={(v) => setChecked(v === true)}
              />
              <label htmlFor={optionId} className="text-sm text-foreground cursor-pointer select-none">
                {options.option.label}
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleResolve(false)}>
              {options?.cancelText ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-action"
              onClick={() => handleResolve(true)}
              variant={options?.destructive ? 'destructive' : 'default'}
            >
              {options?.confirmText ?? 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

function useOpenConfirm(): OpenFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmDialogProvider');
  return ctx;
}

export function useConfirm(): ConfirmFn {
  const open = useOpenConfirm();
  return useCallback<ConfirmFn>(async (options) => (await open(options)).confirmed, [open]);
}

/** A confirm carrying one checkbox, for a choice that rides along with the yes. */
export function useConfirmWithOption(): ConfirmWithOptionFn {
  return useOpenConfirm();
}
